import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CALL_IN_REASONS_QUERY_KEY } from "../../../../components/team/CallInReasons/callInReasonsHooks";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { backendService } from "../../../../services/backend.service";
import { uiVisibleErrorKey } from "../../../../utils/errors";
import type { HiringAbsenceSettings, HiringAbsenceSettingsUpdate } from "../types";
import { HIRING_QUERY_KEY } from "./useHiringPredictionData";

export const HIRING_ABSENCE_SETTINGS_QUERY_KEY = "hiring-absence-settings";

/** What the department's Absences row counts — the "What counts as an absence?" checklist (DAT-850). */
export function useHiringAbsenceSettings() {
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	return useQuery<HiringAbsenceSettings>({
		queryKey: [HIRING_ABSENCE_SETTINGS_QUERY_KEY, departmentId],
		queryFn: async () => (await backendService.getHiringAbsenceSettings(departmentId)).data,
	});
}

/**
 * Saves the whole checklist at once. The grid is refetched because Absences is computed on the server, and
 * the Team → Call-in reasons list because it shows the same per-reason decision.
 *
 * The save only counts as done once the grid is back: that recompute can take ~30s on a large department, and
 * a panel that closed straight away left the old Absences numbers on screen with nothing saying they were about
 * to change — which reads as "the setting didn't work" and gets saved again.
 */
export function useSaveHiringAbsenceSettings() {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	const queryClient = useQueryClient();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	return useMutation<HiringAbsenceSettings, Error, HiringAbsenceSettingsUpdate>({
		mutationFn: async (body) => {
			track("hiring_absence_settings_save_start", { department_id: departmentId, changed_reasons: body.reasons.length });
			return (await backendService.updateHiringAbsenceSettings(departmentId, body)).data;
		},
		// Returned, not fire-and-forget: TanStack Query keeps the mutation pending until it settles, so Save keeps
		// spinning (and the caller's own onSuccess, which closes the panel, waits) until the new numbers are in.
		onSuccess: async (saved, body) => {
			track("hiring_absence_settings_save_success", {
				department_id: departmentId,
				changed_reasons: body.reasons.length,
				counts_pto: saved.countsPto,
				counts_replace_requests: saved.countsReplaceRequests,
			});
			queryClient.setQueryData([HIRING_ABSENCE_SETTINGS_QUERY_KEY, departmentId], saved);
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: [HIRING_QUERY_KEY] }),
				queryClient.invalidateQueries({ queryKey: [CALL_IN_REASONS_QUERY_KEY, departmentId] }),
			]);
		},
		onError: (error) => {
			trackError("hiring_absence_settings_save_error", { department_id: departmentId, error_message: error.message });
			// The backend says why a save was refused (a reason archived or removed meanwhile), so show its reason.
			// DEFAULT: any other failure — network, or a refusal without a reason — gets the generic message.
			showSnackbar(t(uiVisibleErrorKey(error) ?? "hiring.absenceSettings.saveFailed"), "error");
		},
	});
}
